import { AxiosResponse } from 'axios';
import { axiosInstance } from './axiosInstance';

type GetCustomerBy4TJwtResp = {
  token: string;
};

/**
 * Request body for DAP initialization
 */
export interface DapInitializeRequest {
  externalSessionId: string;
}

/**
 * Response from DAP initialization
 */
export interface DapInitializeResponse {
  authorizeURL: string;
  [key: string]: any;
}

/**
 * Response from search accounts
 * Matches the structure of searchAccountsByCustomerId from tmsl.service
 * Only contains email
 */
export interface SearchAccountsResponse {
  email?: string;
}

/**
 * DAP Resource - calls the DAP endpoint in auth-proxy
 * which in turn calls the tms-middleware DAP controller
 */
export const dapResource = {
  /**
   * Initialize DAP and get authorize URL
   * Calls the local service through auth-proxy
   * @param data Request body with externalSessionId
   * @returns Response with authorizeURL
   */
  initialize(data: DapInitializeRequest): Promise<AxiosResponse<DapInitializeResponse>> {
    return axiosInstance.post(`/api/dap/initialize`, data);
  },

  /**
   * Check DAP service health
   * @returns Health status
   */
  health(): Promise<AxiosResponse<{ status: string }>> {
    return axiosInstance.get(`/api/dap/health`);
  },

  /**
   * Get authorize URL from DAP
   * Convenience method that extracts just the URL from the response
   * @param externalSessionId The external session ID (should match correlationId)
   * @param correlationId Optional correlation ID to use for the request
   * @returns Promise resolving to the authorize URL string
   */
  async getAuthorizeUrl(externalSessionId: string, correlationId?: string): Promise<string> {
    const config = correlationId ? { headers: { 'msg-ctx-corr-id': correlationId } } : undefined;
    const response = await axiosInstance.post<DapInitializeResponse>(
      `/api/dap/initialize`,
      { externalSessionId },
      config,
    );
    return response.data.authorizeURL;
  },

  /**
   * Search customers by 4T (family name, given name, mother's name, birth date)
   * Calls auth-proxy which proxies to tms-middleware cman controller (/search-customer-by-4t endpoint)
   * Internally calls searchCustomerByDAPSessionId which retrieves 4T data from EventService
   * @param sessionId The session ID to retrieve 4T data
   * @returns Array of matching customers with id and name
   */
  async searchCustomerBy4T(sessionId: string): Promise<string> {
    const response = await axiosInstance.post<GetCustomerBy4TJwtResp>(`/api/cman/search-customer-by-4t`, {
      sessionId,
    });
    return response.data.token;
  },

  /**
   * Search customers by 4T (family name, given name, mother's name, birth date)
   * Calls auth-proxy which proxies to tms-middleware cman controller (/get-customer-by-4t endpoint)
   * @param jwt JWT containing the identity payload with fourTData
   * @returns JWT containing the matching customers with id and name
   */
  async getCustomerBy4T(jwt: string): Promise<string> {
    const response = await axiosInstance.post<GetCustomerBy4TJwtResp>(`/api/cman/get-customer-by-4t`, { token: jwt });
    return response.data.token;
  },

  /**
   * Search accounts by customer ID
   * Calls auth-proxy which proxies to tms-middleware dap controller
   * @param customerID The Yettel customer ID
   * @returns Array of matching accounts with email
   */
  searchAccounts(customerID: string, jwt: string): Promise<AxiosResponse<SearchAccountsResponse[]>> {
    return axiosInstance.post(`/api/dap/search-accounts`, { customerID, token: jwt });
  },

  /**
   * Get last event status by sessionId
   * @param sessionId The session ID
   * @returns Promise resolving to the status or null
   */
  async getLastEventStatus(sessionId: string): Promise<string | null> {
    const response = await axiosInstance.get<{ status: string | null }>(
      `/api/dap/event/status/${encodeURIComponent(sessionId)}`,
    );
    return response.data.status;
  },
};
